import {
    M43_COMPLETION_HEADER,
    M43_MEASURED_HEADER,
    M43_OPERATION_HEADER,
    type M43Completion,
    type M43Meta,
    type M43Operation,
} from '@m43/shared';

/** The subset of Datadog RUM's resource domain context we care about. */
export type ResourceDomainContext = {
    requestInit?: RequestInit;
    requestInput?: RequestInfo | URL;
};

/**
 * Finds the outgoing request's headers on a RUM resource event context.
 *
 * axios' fetch adapter builds a `Request` with the headers baked in and then calls
 * `fetch(request, init)` with `init.headers` deleted — its own comment says the options
 * are "already reflected in the resolved Request". So the Request is the only place our
 * headers exist, and reading `requestInit.headers` always comes back empty.
 *
 * The init fallback covers plain `fetch(url, init)` callers.
 */
export function resolveRequestHeaders(context: unknown): Headers | undefined {
    const ctx = context as ResourceDomainContext | undefined;
    const input = ctx?.requestInput;
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return input.headers;
    }
    const init = ctx?.requestInit?.headers;
    return init ? new Headers(init) : undefined;
}

/**
 * Recovers the operation declaration from the request that produced a RUM event.
 *
 * Returns undefined for XHR, which cannot expose its own request headers. That is why
 * browser-span tagging only works once `cad.fe_use_axios_fetch.enabled` has swapped a
 * client to the fetch adapter.
 */
export function readM43Meta(context: unknown): M43Meta | undefined {
    const headers = resolveRequestHeaders(context);
    if (!headers || headers.get(M43_MEASURED_HEADER) !== 'true') {
        return undefined;
    }
    const operation = headers.get(M43_OPERATION_HEADER);
    const completion = headers.get(M43_COMPLETION_HEADER);
    if (!operation || !completion) {
        return undefined;
    }
    return { operation: operation as M43Operation, completion: completion as M43Completion };
}
